import React, { FC, ChangeEvent, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { get } from 'lodash';
import { useFormikContext } from 'formik';
import RecentActorsIcon from '@material-ui/icons/RecentActors';

import { SegmentStatus, UpdateUserSegmentInput, UserSegmentFragment } from 'api';
import { ConditionalNumberTooltip, Panel, ValidatedSwitch } from 'components';
import { useNotify } from 'hooks';
import { formatStat } from 'helpers';

import { ASSET_KEYS } from './constants';
import GroupsEdit from './GroupsEdit';
import DepartmentsEdit from './DepartmentsEdit';
import LocationsEdit from './LocationsEdit';
import OtherPeopleEdit from './OtherPeopleEdit';

import Title from './Title';
import Names from './Names';

interface Props {
  segment: UserSegmentFragment;
  working: boolean;
}

const useStyles = makeStyles(theme => ({
  dividerHeader: {
    margin: '12px -16px 24px -16px',
  },
  divider: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  padding: {
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
  },
  blank: {
    color: 'transparent',
  },
  spacer: {
    marginTop: theme.spacing(3),
  },
}));

const Segment: FC<Props> = props => {
  const classes = useStyles();
  const formik = useFormikContext<UpdateUserSegmentInput>();
  const { segment, working } = props;

  const { t } = useTranslation();
  const notify = useNotify();
  const isPopulated = hasAssetKeys();

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  function checkedFn(value: SegmentStatus) {
    return value === SegmentStatus.Active;
  }

  function hasAssetKeys() {
    return !!ASSET_KEYS.map(key => get(segment, key, []).length).reduce(
      (prev, next) => prev + next,
      0
    );
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>, value: boolean) {
    const status = value ? SegmentStatus.Active : SegmentStatus.Disabled;
    formik.setFieldValue('segment_status', status);

    setTimeout(() => {
      void formik.submitForm();
    });
  }

  function renderToggle() {
    return (
      <ValidatedSwitch
        name="segment_status"
        color="primary"
        label={t('common:status')}
        onChange={handleChange}
        checkedFn={checkedFn}
      />
    );
  }

  function renderEditBody() {
    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <DepartmentsEdit segment={segment} onSave={editNotify} />
        <Divider className={classes.divider} />
        <LocationsEdit segment={segment} onSave={editNotify} />
        <Divider className={classes.divider} />
        <GroupsEdit segment={segment} onSave={editNotify} />
        <Divider className={classes.divider} />
        <OtherPeopleEdit segment={segment} onSave={editNotify} />
        <Divider className={classes.divider} />
      </Fragment>
    );
  }

  function renderCounts() {
    const { registered_count, third_party_count } = segment;
    const total = registered_count + third_party_count;

    return (
      <Grid container spacing={2}>
        <Grid item xs={2}>
          <Typography color="textSecondary" variant="subtitle1">
            {t('common:total')}
          </Typography>
          <ConditionalNumberTooltip value={total}>
            <Typography variant="h6">{formatStat(total)}</Typography>
          </ConditionalNumberTooltip>
        </Grid>
        <Grid item xs={2}>
          <Typography color="textSecondary" variant="subtitle1">
            {t('common:user', { count: registered_count })}
          </Typography>
          <ConditionalNumberTooltip value={total}>
            <Typography variant="h6">{formatStat(registered_count)}</Typography>
          </ConditionalNumberTooltip>
        </Grid>
        <Grid item xs={2}>
          <Typography color="textSecondary" variant="subtitle1">
            {t('common:email', { count: third_party_count })}
          </Typography>
          <ConditionalNumberTooltip value={total}>
            <Typography variant="h6">{formatStat(third_party_count)}</Typography>
          </ConditionalNumberTooltip>
        </Grid>
      </Grid>
    );
  }

  function renderViewBody() {
    return (
      <Fragment>
        <Divider className={classes.dividerHeader} />
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <RecentActorsIcon fontSize="large" className={classes.blank} />
          </Grid>
          <Grid item xs>
            {renderCounts()}
            {hasAssetKeys() && (
              <Grid className={classes.spacer} container spacing={2}>
                <Names
                  title={t('common:department_plural')}
                  listKey="departments"
                  segment={segment}
                />
                <Names title={t('common:location_plural')} listKey="locations" segment={segment} />
                <Names title={`${t('common:group_plural')}`} listKey="groups" segment={segment} />
              </Grid>
            )}
          </Grid>
          <Grid item>
            <RecentActorsIcon fontSize="large" className={classes.blank} />
          </Grid>
        </Grid>
      </Fragment>
    );
  }

  function renderHeader(editing: boolean) {
    if (editing) {
      return <Title working={working} activeIcon={isPopulated} mode="edit" />;
    }

    return (
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <RecentActorsIcon fontSize="large" color={isPopulated ? 'primary' : 'disabled'} />
        </Grid>
        <Grid item xs>
          <Typography variant="subtitle2">{segment.title}</Typography>
        </Grid>
      </Grid>
    );
  }

  function renderBody(editing: boolean) {
    return editing ? renderEditBody() : renderViewBody();
  }

  return <Panel header={renderHeader} body={renderBody} toggle={renderToggle} />;
};

export default Segment;
